import { useCallback, useMemo, useState } from "react";
import {
  type BaselineKind,
  DEFAULT_SHRINKAGE,
  type Ranked,
  move,
  moveLabel,
  planOrder,
  rank,
} from "@trackmark/core";
import { Link } from "@tanstack/react-router";
import { RankTable, rankTableStyles as table } from "../design/RankTable.js";
import { useMobile } from "../useMobile.js";
import { RatingStrip } from "../design/RatingStrip.js";
import { AlbumRankings, type AlbumRow } from "./AlbumRankings.js";
import { ArtistTable, type ArtistRow, orderedArtists } from "./ArtistTable.js";
import styles from "./Rankings.module.css";

export interface SongRow {
  id: number;
  title: string;
  artist: string;
  /** So the artist column can go to the artist page. */
  artistId: number;
  album: string;
  /** So the row can go to the record. Albums have URLs now. */
  albumId: number;
  year: number | null;
  score: number | null;
  ratedAt: string | null;
  art: string | null;
  /** Position inside the tier. null = unplaced — unmeasured, and it says so in the row. */
  orderKey: string | null;
  /** Rank at the chosen baseline, and whether this is a first-ever rating inside the window. */
  previousRank?: number | null;
  isNew?: boolean;
}

export type { ArtistRow };

export type RankingTab = "songs" | "albums" | "artists";

/**
 * `MOVE` is INSERTED when a baseline is chosen, not reserved against the day one might be.
 * §I3 is explicit that Phase 4 must not hold a gutter open for a column that is not there.
 */
const SONG_COLUMNS = "64px 24px 1fr 190px 120px 42px 52px 84px 20px";
const SONG_COLUMNS_WITH_MOVE = "64px 24px 1fr 190px 120px 42px 52px 84px 56px 20px";

/**
 * Mobile (design/README.md §13): 52px rows — rank, 40px artwork, title and artist STACKED, score
 * in Anton 22px. Not the desktop table with columns hidden: eight columns minus five is a table
 * with holes in it, and the stack is what makes 390px readable.
 */
const SONG_COLUMNS_MOBILE = "34px 40px 1fr 34px";
const MOBILE_ROW_HEIGHT = 52;

const dateLabel = (iso: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const M = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  return `${String(d.getUTCDate()).padStart(2, "0")} ${M[d.getUTCMonth()]} ${String(d.getUTCFullYear() % 100).padStart(2, "0")}`;
};

export interface BaselineOption {
  kind: BaselineKind;
  label: string;
  /** null = offered but unresolvable, e.g. no last session in a library with no history. */
  at: string | null;
}

export interface RankingsProps {
  songs: SongRow[];
  albums: AlbumRow[];
  artists: ArtistRow[];
  collection: { totalRated: number; mean: number | null };
  settings?: typeof DEFAULT_SHRINKAGE;
  /** The MOVE baselines on offer, resolved by the server. */
  baselines?: BaselineOption[];
  baseline?: BaselineKind | null;
  onBaseline?: (kind: BaselineKind | null) => void;
  /**
   * Persists a dragged tier and answers with the keys it settled on. Absent in the fixtures, where
   * dragging is a rendering concern rather than a write.
   */
  onOrder?: (tier: number, songIds: number[]) => Promise<Record<number, string> | null>;
}

const TITLES: Record<RankingTab, string> = {
  songs: "Song rankings",
  albums: "Albums",
  artists: "Artist rankings",
};

/**
 * Song, album and artist rankings.
 *
 * Ranking is derived here with the shared `rank()` — the same function the server's window
 * expression is tested against — so the repeat flag comes from the neighbouring row rather than
 * being stored, and a tie group reads as a block.
 */
export function Rankings({
  songs,
  albums,
  artists,
  collection,
  settings = DEFAULT_SHRINKAGE,
  baselines = [],
  baseline = null,
  onBaseline,
  onOrder,
}: RankingsProps) {
  const mobile = useMobile();
  const [tab, setTab] = useState<RankingTab>("songs");
  /*
   * Local rather than in the URL, unlike the library's.
   *
   * The rankings' own state — the tab, the score floor, the as-of baseline — is already local
   * here; putting one of five controls in the search params would make exactly one of them
   * survive a reload, which reads as a bug rather than a feature.
   */
  const [{ sort: artistSort, direction: artistDirection }, setArtistSort] = useState<{
    sort: string;
    direction: "asc" | "desc";
  }>({ sort: "rank", direction: "asc" });
  const [minScore, setMinScore] = useState<number | null>(null);
  /**
   * Keys written since this screen loaded, over the top of the ones the server sent.
   *
   * A drag has to reorder the table on the same frame — reading the answer back would make the
   * gesture lag a round trip — so the client generates the keys with the same `planOrder` the
   * server runs, and reconciles with the response when it lands.
   */
  const [placed, setPlaced] = useState<Map<number, string>>(new Map());
  const [dragging, setDragging] = useState<number | null>(null);

  const filtered = useMemo(
    () => (minScore === null ? songs : songs.filter((s) => s.score !== null && s.score >= minScore)),
    [songs, minScore],
  );

  const keyOf = useCallback(
    (s: SongRow) => placed.get(s.id) ?? s.orderKey ?? null,
    [placed],
  );

  const rankedSongs = useMemo(() => rank(filtered, (s) => s.score, keyOf), [filtered, keyOf]);

  /**
   * The ordering-blind rank, which is the ONLY thing MOVE is allowed to see.
   *
   * MOVE means "my opinion changed". The baseline it compares against is reconstructed from
   * `rating_event`, which records values and knows nothing about ordering — so feeding it the
   * displayed rank would report an afternoon of dragging as a hundred songs moving, permanently,
   * on the column's first use. Ordering permutes rows inside a tier; it never moves the tier.
   */
  const unorderedRank = useMemo(() => {
    const out = new Map<number, number | null>();
    for (const r of rank(filtered, (s) => s.score)) out.set(r.item.id, r.rank);
    return out;
  }, [filtered]);

  /** Group sizes, so a lone row shows no grip and an ordered group keeps its vermilion edge. */
  const groupSize = useMemo(() => {
    const sizes = new Map<number, number>();
    for (const r of rankedSongs) if (r.item.score !== null) sizes.set(r.item.score, (sizes.get(r.item.score) ?? 0) + 1);
    return sizes;
  }, [rankedSongs]);

  const onDrop = (target: Ranked<SongRow>) => {
    if (dragging === null || target.rank === null || target.item.score === null) return;
    const tier = target.item.score;
    // The group is the tier: `minScore` filters whole tiers in or out, so what is on screen is
    // never half of one.
    const group = rankedSongs.filter((r) => r.item.score === tier);
    const from = group.findIndex((r) => r.item.id === dragging);
    const to = group.findIndex((r) => r.item.id === target.item.id);
    if (from < 0 || to < 0 || from === to) return;

    const items = group.map((r) => r.item);
    const [moved] = items.splice(from, 1);
    items.splice(to, 0, moved!);

    // Paint first. `planOrder` is deterministic, so these are the keys the server will arrive at
    // from the same inputs — the reconcile below is for the case it does something more, like
    // rebalancing a tier whose keys have grown.
    const optimistic = new Map(placed);
    for (const { index, key } of planOrder(items.map(keyOf))) optimistic.set(items[index]!.id, key);
    setPlaced(optimistic);
    setDragging(null);

    onOrder?.(tier, items.map((s) => s.id))
      .then((keys) => {
        if (keys) setPlaced((prev) => new Map([...prev, ...Object.entries(keys).map(([id, k]) => [Number(id), k] as const)]));
      })
      .catch(() => {
        // The write failed, so the table must stop claiming it happened. Dropping back to the
        // server's keys is the honest state — silently keeping a local order the database does
        // not have is the bug this whole change exists to fix.
        setPlaced(new Map());
      });
  };

  return (
    <div className={styles.page}>
      <div className={styles.title}>{TITLES[tab]}</div>

      <div className={styles.tabs}>
        {/*
          * Songs only on mobile. The albums view is a covers grid and the artists view is a
          * seven-column table with a shrinkage caption — both are desktop work, and a tab that
          * leads to a wall is worse than a tab that is not there.
          */}
        {(mobile ? (["songs"] as const) : (["songs", "albums", "artists"] as const)).map((t) => (
          <button key={t} type="button" className={styles.tab} data-active={t === tab} onClick={() => setTab(t)}>
            {t}
          </button>
        ))}
      </div>

      {tab === "albums" ? (
        <AlbumRankings albums={albums} />
      ) : tab === "songs" ? (
        <>
          <div className={styles.filters}>
            <span>MINIMUM SCORE</span>
            {[null, 7, 8, 9, 10].map((n) => (
              <button
                key={String(n)}
                type="button"
                className={styles.filter}
                data-active={minScore === n}
                onClick={() => setMinScore(n)}
              >
                {n === null ? "ANY" : n}
              </button>
            ))}
            <span className={styles.count} data-testid="row-count">
              {filtered.length.toLocaleString()} SONGS
            </span>
          </div>

          {baselines.length > 0 && (
            <div className={styles.filters} data-testid="baselines">
              {/* The "since when" problem dissolves rather than being answered: the system stops
                  guessing a baseline and the user picks one (§I3). OFF is the default, because a
                  MOVE column nobody asked for is a comparison against a date nobody chose. */}
              <span>MOVE SINCE</span>
              <button
                type="button"
                className={styles.filter}
                data-active={baseline === null}
                onClick={() => onBaseline?.(null)}
              >
                OFF
              </button>
              {baselines.map((b) => (
                <button
                  key={b.kind}
                  type="button"
                  className={styles.filter}
                  data-active={baseline === b.kind}
                  // Unresolvable is disabled rather than hidden: "no last session yet" is
                  // information about the library, and a vanishing control is not.
                  disabled={b.at === null}
                  title={b.at === null ? "No rating history to compare against yet" : undefined}
                  onClick={() => onBaseline?.(b.kind)}
                >
                  {b.label}
                </button>
              ))}
            </div>
          )}

          <RankTable
            rows={rankedSongs}
            columns={mobile ? SONG_COLUMNS_MOBILE : baseline === null ? SONG_COLUMNS : SONG_COLUMNS_WITH_MOVE}
            rowHeight={mobile ? MOBILE_ROW_HEIGHT : undefined}
            rowKey={(r) => r.item.id}
            rowProps={(r) => ({
              "data-state": r.item.score === null ? "unrated" : undefined,
              // The vermilion left edge marks a tier the user has had a hand in.
              "data-ordered": keyOf(r.item) !== null ? "true" : undefined,
              "data-unplaced": r.unplaced ? "true" : undefined,
              "data-dragging": dragging === r.item.id ? "true" : undefined,
              "data-rank": String(r.rank ?? ""),
            })}
            header={
              mobile ? (
                <>
                  <div>RANK</div>
                  <div />
                  <div>TITLE</div>
                  <div style={{ textAlign: "right" }}>SCR</div>
                </>
              ) : (
                <>
                  <div>RANK</div>
                  <div />
                  <div>TITLE</div>
                  <div>ARTIST</div>
                  <div>RATING</div>
                  <div style={{ textAlign: "right" }}>SCR</div>
                  <div style={{ textAlign: "right" }}>YEAR</div>
                  <div style={{ textAlign: "right" }}>RATED</div>
                  {baseline !== null && <div style={{ textAlign: "right" }}>MOVE</div>}
                  <div />
                </>
              )
            }
            renderRow={(r) =>
              mobile ? (
                <>
                  <div
                    className={styles.mobileRank}
                    data-unranked={r.rank === null || undefined}
                    data-unplaced={r.unplaced || undefined}
                  >
                    {r.rank ?? "—"}
                  </div>
                  <Link
                    to="/album/$albumId"
                    params={{ albumId: String(r.item.albumId) }}
                    aria-label={`Go to ${r.item.album}`}
                    data-testid="song-album-link"
                  >
                    {r.item.art ? (
                      <img className={styles.mobileArt} src={r.item.art} alt="" />
                    ) : (
                      <div className={`${styles.mobileArt} ${table.artFallback}`}>{r.item.album.slice(0, 8)}</div>
                    )}
                  </Link>
                  {/* Stacked, because 390px has no room for two columns of text and the artist is
                      half of knowing which song this is. */}
                  <div className={styles.mobileStack}>
                    <div className={styles.mobileTitle}>{r.item.title}</div>
                    <div className={styles.mobileArtist}>
                      {r.item.artist}
                      {r.unplaced && (
                        <span className={styles.unplaced} data-testid="unplaced">
                          UNPLACED
                        </span>
                      )}
                    </div>
                  </div>
                  <div className={styles.mobileScore} data-unrated={r.item.score === null || undefined}>
                    {r.item.score ?? "—"}
                  </div>
                </>
              ) : (
              <>
                {/* An unplaced row shares its numeral with its siblings, so it greys like a
                    repeat. The word beside the title is what stops that grey reading as "tied
                    for 15th" — these rows are not tied with each other, they are unmeasured. */}
                <div
                  className={table.rank}
                  data-repeat={r.repeat || r.unplaced || undefined}
                  data-unranked={r.rank === null || undefined}
                >
                  {r.rank ?? "—"}
                </div>
                {/* The artwork IS the album, so it is what goes to the record; the artist column
                    goes to the artist page. Two destinations, each on the thing it names. */}
                <Link
                  to="/album/$albumId"
                  params={{ albumId: String(r.item.albumId) }}
                  aria-label={`Go to ${r.item.album}`}
                  data-testid="song-album-link"
                >
                  {r.item.art ? (
                    <img className={table.art} src={r.item.art} alt="" />
                  ) : (
                    <div className={table.artFallback}>{r.item.album.slice(0, 8)}</div>
                  )}
                </Link>
                <div className={styles.titleCell}>
                  <span className={table.title}>{r.item.title}</span>
                  {r.unplaced && (
                    <span className={styles.unplaced} data-testid="unplaced">
                      UNPLACED
                    </span>
                  )}
                </div>
                <Link
                  to="/artist/$artistId"
                  params={{ artistId: String(r.item.artistId) }}
                  className={table.secondaryLink}
                  data-testid="song-artist-link"
                >
                  {r.item.artist}
                </Link>
                <RatingStrip value={r.item.score} />
                {r.item.score === null ? (
                  <div className={table.unr}>UNR</div>
                ) : (
                  <div className={table.score}>{r.item.score}</div>
                )}
                <div className={table.meta}>{r.item.year ?? "—"}</div>
                <div className={table.meta}>{dateLabel(r.item.ratedAt)}</div>
                {baseline !== null &&
                  (() => {
                    // The ordering-blind rank, never the displayed one — see `unorderedRank`.
                    const m = move(unorderedRank.get(r.item.id) ?? null, r.item.previousRank ?? null, r.item.isNew === true);
                    return (
                      <div className={styles.move} data-move={m.kind} data-testid="move">
                        {moveLabel(m)}
                      </div>
                    );
                  })()}
                <button
                  type="button"
                  className={table.grip}
                  aria-label={`Reorder ${r.item.title}`}
                  data-hidden={(groupSize.get(r.item.score ?? -1) ?? 0) < 2 || undefined}
                  data-ordered={keyOf(r.item) !== null || undefined}
                  draggable
                  onDragStart={() => setDragging(r.item.id)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDrop(r)}
                >
                  ⠿
                </button>
              </>
              )
            }
          />
        </>
      ) : (
        <>
          <ArtistTable
            rows={orderedArtists(artists, collection, settings, artistSort, artistDirection)}
            collection={collection}
            settings={settings}
            // The rankings' artist tab is the ranking: it opens in rank order, and the headings
            // sort it the same way the library's do.
            sort={artistSort}
            direction={artistDirection}
            onSort={(sort, direction) => setArtistSort({ sort, direction })}
          />
        </>
      )}
    </div>
  );
}
